import { useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { pipeline } from "@huggingface/transformers";

const IDVerification = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [verificationResult, setVerificationResult] = useState<any>(null);
  const { toast } = useToast();

  const verifyDocument = async (file: File) => {
    setIsProcessing(true);
    try {
      // Initialize the image classification pipeline
      const classifier = await pipeline(
        "image-classification",
        "onnx-community/mobilenetv4_conv_small.e2400_r224_in1k",
        { device: "webgpu" }
      );

      // Convert file to URL for processing
      const imageUrl = URL.createObjectURL(file);
      const results = await classifier(imageUrl);

      setVerificationResult(results);
      toast({
        title: "Verification Complete",
        description: "Document has been processed successfully.",
      });
    } catch (error) {
      console.error("Verification error:", error);
      toast({
        variant: "destructive",
        title: "Verification Failed",
        description: "There was an error processing the document.",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">ID Verification</h1>
      
      <Card>
        <CardHeader>
          <CardTitle>Document Verification System</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) verifyDocument(file);
              }}
              className="hidden"
              id="document-upload"
            />
            <label
              htmlFor="document-upload"
              className="block w-full p-4 border-2 border-dashed rounded-lg text-center cursor-pointer hover:border-primary"
            >
              Drop ID document here or click to upload
            </label>

            {isProcessing && (
              <div className="flex items-center justify-center space-x-2">
                <Loader2 className="animate-spin" />
                <span>Processing document...</span>
              </div>
            )}

            {verificationResult && (
              <div className="mt-4 p-4 bg-secondary rounded-lg">
                <h3 className="font-semibold mb-2">Verification Results:</h3>
                <ul className="space-y-2">
                  {verificationResult.map((result: any, index: number) => (
                    <li key={index} className="flex items-center space-x-2">
                      {result.score > 0.8 ? (
                        <CheckCircle className="text-green-500" />
                      ) : (
                        <AlertCircle className="text-yellow-500" />
                      )}
                      <span>{result.label}: {(result.score * 100).toFixed(2)}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IDVerification;